<script setup>
import { reactive, ref, shallowRef } from 'vue';
import UiFormField from './UiFormField.vue';
import { matches, required, strongPassword } from '@/utils/validation';

// "New password" + "Confirm password" with the strong-password rule, for the
// pages where someone chooses their own password. Emits the password once
// both fields are valid.
defineProps({
  submitText: { type: String, default: 'Save password' },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
});
const emit = defineEmits(['submit']);

const formRef = ref(null);
const form = reactive({ password: '', confirm: '' });
const reveal = shallowRef(false);

async function submit() {
  const { valid } = await formRef.value.validate();
  if (valid) emit('submit', form.password);
}
</script>

<template>
  <v-form ref="formRef" class="form-stack" @submit.prevent="submit">
    <v-alert v-if="error" type="error" variant="tonal" density="compact">{{ error }}</v-alert>

    <UiFormField
      label="New password"
      description="At least 8 characters with upper and lower case letters, a number and a symbol."
      v-slot="{ id }"
    >
      <v-text-field
        :id="id"
        v-model="form.password"
        name="new-password"
        :type="reveal ? 'text' : 'password'"
        autocomplete="new-password"
        :rules="[required, strongPassword]"
        :append-inner-icon="reveal ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
        hide-details="auto"
        autofocus
        @click:append-inner="reveal = !reveal"
      />
    </UiFormField>

    <UiFormField label="Confirm password" v-slot="{ id }">
      <v-text-field
        :id="id"
        v-model="form.confirm"
        name="confirm-password"
        :type="reveal ? 'text' : 'password'"
        autocomplete="new-password"
        :rules="[required, matches(() => form.password, 'Passwords do not match.')]"
        hide-details="auto"
      />
    </UiFormField>

    <v-btn type="submit" color="primary" block :loading="loading">{{ submitText }}</v-btn>
  </v-form>
</template>
